import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowUp, ArrowDown, Users, DollarSign, TrendingUp, Eye, Check, X, Ban, Trash2, ArrowUpDown, ExternalLink, MapPin, Search, Phone, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { getEducationLabel } from '../lib/constants';
import { formatManilaDate } from '../lib/dateUtils';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import PesoBadge from '../components/PesoBadge';
import AdminBannersSection from '../components/AdminBannersSection';
import AdminServiceModerationSection from '../components/AdminServiceModerationSection';
import AdminServicesAnalyticsSection from '../components/AdminServicesAnalyticsSection';

const CEODashboard = ({ profile, setProfile, hideNavigation = false }) => {
  const navigate = useNavigate();
  
  // State for metrics
  const [metrics, setMetrics] = useState(null);
  const [smsCredits, setSmsCredits] = useState(null);  // Semaphore SMS credit balance
  const [userGrowth, setUserGrowth] = useState([]);
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [usersByTown, setUsersByTown] = useState([]);
  const [latestSignupsSeekers, setLatestSignupsSeekers] = useState([]);
  const [latestSignupsEmployers, setLatestSignupsEmployers] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [topSeekers, setTopSeekers] = useState([]);
  const [topSpenders, setTopSpenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 10 });
  
  // Modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showAllReportsModal, setShowAllReportsModal] = useState(false);
  const [allReports, setAllReports] = useState([]);
  const [showAllVerificationsModal, setShowAllVerificationsModal] = useState(false);
  const [allVerifications, setAllVerifications] = useState([]);
  const [showAllSignupsModal, setShowAllSignupsModal] = useState(false);
  const [allSignups, setAllSignups] = useState([]);
  const [signupSearchQuery, setSignupSearchQuery] = useState('');
  const [signupSearchLoading, setSignupSearchLoading] = useState(false);
  const [allSignupsTotal, setAllSignupsTotal] = useState(0);
  // Which role the "View All" modal is currently filtered to ('seeker' | 'employer' | null).
  // Set by the View All buttons on the per-role signup tables; passed to the backend
  // and used to label the modal title.
  const [signupsModalRole, setSignupsModalRole] = useState(null);
  
  // Promotion stats state
  const [promoStats, setPromoStats] = useState(null);

  // Jobs health state (stale jobs alert)

  // Seeker applications state
  const [seekerApplications, setSeekerApplications] = useState([]);
  const [loadingSeekerApps, setLoadingSeekerApps] = useState(false);
  // User certifications state (for admin profile modal)
  const [userCertifications, setUserCertifications] = useState([]);
  // Manual verify state
  const [showManualVerifyDialog, setShowManualVerifyDialog] = useState(false);
  const [manualVerifying, setManualVerifying] = useState(false);

  // Debounce ref for realtime refreshes
  const realtimeDebounceRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  // Top seekers pagination state
  const [topSeekersLimit, setTopSeekersLimit] = useState(10);
  const [loadingMoreSeekers, setLoadingMoreSeekers] = useState(false);

  // Latest signups pagination state (per role — independent so each table loads more independently)
  const [latestSignupsSeekersLimit, setLatestSignupsSeekersLimit] = useState(10);
  const [loadingMoreSeekerSignups, setLoadingMoreSeekerSignups] = useState(false);
  const [latestSignupsEmployersLimit, setLatestSignupsEmployersLimit] = useState(10);
  const [loadingMoreEmployerSignups, setLoadingMoreEmployerSignups] = useState(false);

  // Recent jobs pagination state
  const [recentJobsLimit, setRecentJobsLimit] = useState(10);
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);
  
  // Sorting state for tables
  const [sortConfig, setSortConfig] = useState({
    signupsSeekers: { key: 'created_at', direction: 'desc' },
    signupsEmployers: { key: 'created_at', direction: 'desc' },
    recentJobs: { key: 'created_at', direction: 'desc' },
    verifications: { key: 'created_at', direction: 'desc' },
    reports: { key: 'created_at', direction: 'desc' },
    seekers: { key: 'rank', direction: 'asc' },
    spenders: { key: 'rank', direction: 'asc' },
    allReports: { key: 'created_at', direction: 'desc' },
    allVerifications: { key: 'created_at', direction: 'desc' }
  });

  // Fetch promotion stats
  const fetchPromoStats = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      
      const response = await api.get('/api/admin/promotion-stats', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setPromoStats(response.data);
    } catch (error) {
      console.error('Error fetching promotion stats:', error);
    }
  };

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in as admin');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${session.session.access_token}` };

      // SMS credit balance (admin-only, cached server-side). Fire-and-forget so a
      // Semaphore hiccup never blocks or fails the dashboard load.
      api.get('/api/admin/sms-credits', { headers })
        .then(r => setSmsCredits(r.data))
        .catch(() => setSmsCredits({ available: false }));

      // Also fetch promo stats
      fetchPromoStats();

      // Fetch all data in parallel with individual error handling and progress tracking
      const apiCalls = [
        { name: 'metrics', call: api.get('/api/admin/dashboard/metrics', { headers }) },
        { name: 'user-growth', call: api.get('/api/admin/dashboard/user-growth', { headers }) },
        { name: 'revenue-history', call: api.get('/api/admin/dashboard/revenue-history', { headers }) },
        { name: 'users-by-town', call: api.get('/api/admin/dashboard/users-by-town', { headers }) },
        { name: 'latest-signups-seekers', call: api.get(`/api/admin/dashboard/latest-signups?role=seeker&limit=${latestSignupsSeekersLimit}`, { headers }) },
        { name: 'latest-signups-employers', call: api.get(`/api/admin/dashboard/latest-signups?role=employer&limit=${latestSignupsEmployersLimit}`, { headers }) },
        { name: 'recent-jobs', call: api.get(`/api/admin/dashboard/recent-jobs?limit=${recentJobsLimit}`, { headers }) },
        { name: 'pending-verifications', call: api.get('/api/admin/dashboard/pending-verifications', { headers }) },
        { name: 'recent-reports', call: api.get('/api/admin/dashboard/recent-reports', { headers }) },
        { name: 'top-seekers', call: api.get(`/api/admin/dashboard/top-seekers?limit=${topSeekersLimit}`, { headers }) },
        { name: 'top-spenders', call: api.get('/api/admin/dashboard/top-spenders', { headers }) }
      ];

      setLoadingProgress({ current: 0, total: apiCalls.length });

      // Track completed calls for progress
      let completed = 0;
      const wrappedCalls = apiCalls.map(({ name, call }) => {
        return call.then(result => {
          completed++;
          setLoadingProgress({ current: completed, total: apiCalls.length });
          console.log(`✅ Loaded ${name} (${completed}/${apiCalls.length})`);
          return result;
        }).catch(error => {
          completed++;
          setLoadingProgress({ current: completed, total: apiCalls.length });
          console.error(`❌ Failed ${name} (${completed}/${apiCalls.length})`, error);
          throw error;
        });
      });

      const results = await Promise.allSettled(wrappedCalls);

      // Process results with fallbacks
      const [
        metricsRes,
        userGrowthRes,
        revenueHistoryRes,
        usersByTownRes,
        latestSignupsSeekersRes,
        latestSignupsEmployersRes,
        recentJobsRes,
        pendingVerificationsRes,
        recentReportsRes,
        topSeekersRes,
        topSpendersRes
      ] = results;

      // Set data — only overwrite with empty fallbacks on initial load
      // On subsequent refreshes, keep existing data if the call fails
      const isInitial = !initialLoadDoneRef.current;

      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data);
      } else if (isInitial) {
        console.error('Metrics failed:', metricsRes.reason);
        setMetrics({ total_users: 0, total_revenue: 0, total_jobs: 0, total_gigs: 0 });
      }

      if (userGrowthRes.status === 'fulfilled') {
        setUserGrowth(userGrowthRes.value.data.data);
      } else if (isInitial) {
        console.error('User growth failed:', userGrowthRes.reason);
        setUserGrowth([]);
      }

      if (revenueHistoryRes.status === 'fulfilled') {
        setRevenueHistory(revenueHistoryRes.value.data.data);
      } else if (isInitial) {
        console.error('Revenue history failed:', revenueHistoryRes.reason);
        setRevenueHistory([]);
      }

      if (usersByTownRes.status === 'fulfilled') {
        setUsersByTown(usersByTownRes.value.data.data);
      } else if (isInitial) {
        console.error('Users by town failed:', usersByTownRes.reason);
        setUsersByTown([]);
      }

      if (latestSignupsSeekersRes.status === 'fulfilled') {
        setLatestSignupsSeekers(latestSignupsSeekersRes.value.data.signups);
      } else if (isInitial) {
        console.error('Latest seeker signups failed:', latestSignupsSeekersRes.reason);
        setLatestSignupsSeekers([]);
      }

      if (latestSignupsEmployersRes.status === 'fulfilled') {
        setLatestSignupsEmployers(latestSignupsEmployersRes.value.data.signups);
      } else if (isInitial) {
        console.error('Latest employer signups failed:', latestSignupsEmployersRes.reason);
        setLatestSignupsEmployers([]);
      }

      if (recentJobsRes.status === 'fulfilled') {
        setRecentJobs(recentJobsRes.value.data.jobs);
      } else if (isInitial) {
        console.error('Recent jobs failed:', recentJobsRes.reason);
        setRecentJobs([]);
      }

      if (pendingVerificationsRes.status === 'fulfilled') {
        setPendingVerifications(pendingVerificationsRes.value.data.verifications);
      } else if (isInitial) {
        console.error('Pending verifications failed:', pendingVerificationsRes.reason);
        setPendingVerifications([]);
      }

      if (recentReportsRes.status === 'fulfilled') {
        setRecentReports(recentReportsRes.value.data.reports);
      } else if (isInitial) {
        console.error('Recent reports failed:', recentReportsRes.reason);
        setRecentReports([]);
      }

      if (topSeekersRes.status === 'fulfilled') {
        setTopSeekers(topSeekersRes.value.data.seekers);
      } else if (isInitial) {
        console.error('Top seekers failed:', topSeekersRes.reason);
        setTopSeekers([]);
      }

      if (topSpendersRes.status === 'fulfilled') {
        setTopSpenders(topSpendersRes.value.data.spenders);
      } else if (isInitial) {
        console.error('Top spenders failed:', topSpendersRes.reason);
        setTopSpenders([]);
      }

      initialLoadDoneRef.current = true;

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Debounced refresh — collapses multiple rapid realtime events into one refresh
  const debouncedRefresh = useCallback((source) => {
    if (realtimeDebounceRef.current) {
      clearTimeout(realtimeDebounceRef.current);
    }
    realtimeDebounceRef.current = setTimeout(() => {
      console.log(`🔄 Debounced refresh triggered by: ${source}`);
      fetchDashboardData();
    }, 5000);
  }, []);

  useEffect(() => {
    fetchDashboardData();

    // Set up realtime subscriptions for live updates (debounced)
    const profilesChannel = supabase
      .channel('ceo-dashboard-profiles')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        debouncedRefresh('profiles');
      })
      .subscribe();

    const paymentsChannel = supabase
      .channel('ceo-dashboard-payments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payments' }, () => {
        debouncedRefresh('payments');
      })
      .subscribe();

    return () => {
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
      }
      supabase.removeChannel(profilesChannel);
      supabase.removeChannel(paymentsChannel);
    };
  }, []);

  // Helper function to navigate to reported post
  const navigateToReportedPost = (report) => {
    if (!report.post) {
      toast.error('Post details not available');
      return;
    }
    
    const itemType = report.reported_item_type;
    const postId = report.post.id;
    
    if (itemType === 'job') {
      navigate(`/jobs/${postId}`);
    } else if (itemType === 'gig') {
      navigate(`/gigs/${postId}`);
    } else {
      toast.error('Unknown post type');
    }
  };

  const fetchProfileDetails = async (userId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = { Authorization: `Bearer ${session.session.access_token}`, 'X-Admin-View': 'true' };
      const response = await api.get(`/api/profiles/${userId}`, { headers: authHeaders });
      const profileData = response.data.profile;

      // Fetch credit balance for admin view (profile endpoint only includes for self-viewing)
      try {
        const creditsResp = await api.get(`/api/admin/credits/${userId}`, { headers: authHeaders });
        if (creditsResp.data) {
          profileData.credits = creditsResp.data.balance ?? 0;
          profileData.unlimited_reveals = creditsResp.data.unlimited ?? false;
          profileData.unlimited_reveals_until = creditsResp.data.unlimited_until ?? null;
        }
      } catch { /* credits endpoint may not exist, fall through */ }

      setSelectedProfile(profileData);

      // Fetch certifications for this user
      try {
        const certsResp = await api.get(`/api/admin/certifications/user/${userId}`, { headers: authHeaders });
        setUserCertifications(certsResp.data.certifications || []);
      } catch (error) {
        console.error('Failed to fetch certifications:', error);
        setUserCertifications([]);
      }

      // If job seeker, fetch their applications
      if (profileData.role === 'job_seeker' || profileData.role === 'contractor') {
        setLoadingSeekerApps(true);
        try {
          const appsResponse = await api.get(`/api/admin/dashboard/seeker-applications/${userId}`, {
            headers: { Authorization: `Bearer ${session.session.access_token}` }
          });
          setSeekerApplications(appsResponse.data.applications || []);
        } catch (error) {
          console.error('Failed to fetch applications:', error);
          setSeekerApplications([]);
        } finally {
          setLoadingSeekerApps(false);
        }
      } else {
        setSeekerApplications([]);
      }

      setShowProfileModal(true);
    } catch (error) {
      toast.error('Failed to load profile');
      console.error('Profile fetch error:', error);
    }
  };

  const handleManualVerify = async () => {
    if (!selectedProfile) return;
    setManualVerifying(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/manual-verify/${selectedProfile.id}`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success(`${selectedProfile.name || 'User'} has been verified!`);
      setShowManualVerifyDialog(false);
      // Refresh profile data to show updated verified status
      fetchProfileDetails(selectedProfile.id);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to verify user';
      toast.error(msg);
    } finally {
      setManualVerifying(false);
    }
  };

  const fetchAllReports = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get('/api/admin/dashboard/all-reports', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setAllReports(response.data.reports);
      setShowAllReportsModal(true);
    } catch (error) {
      toast.error('Failed to load all reports');
    }
  };

  const fetchAllVerifications = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get('/api/admin/dashboard/all-verifications', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setAllVerifications(response.data.verifications);
      setShowAllVerificationsModal(true);
    } catch (error) {
      toast.error('Failed to load all verifications');
    }
  };

  // `role` is 'seeker' | 'employer' | null. When opening from a View All button,
  // pass the role to filter; subsequent searches inside the modal reuse the same
  // role (read from signupsModalRole state when re-called from realtime refresh
  // or post-ban refresh).
  const fetchAllSignups = async (searchQuery = '', role = signupsModalRole) => {
    setSignupSearchLoading(true);
    setSignupsModalRole(role);
    try {
      const { data: session } = await supabase.auth.getSession();
      const params = new URLSearchParams({ limit: '100' });
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (role === 'seeker' || role === 'employer') {
        params.append('role', role);
      }
      const response = await api.get(`/api/admin/dashboard/all-signups?${params.toString()}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setAllSignups(response.data.signups);
      setAllSignupsTotal(response.data.total);
      setShowAllSignupsModal(true);
    } catch (error) {
      toast.error('Failed to load all signups');
    } finally {
      setSignupSearchLoading(false);
    }
  };

  const handleSignupSearch = (e) => {
    e.preventDefault();
    fetchAllSignups(signupSearchQuery, signupsModalRole);
  };

  const loadMoreSeekers = async () => {
    const newLimit = topSeekersLimit + 10;
    setLoadingMoreSeekers(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get(`/api/admin/dashboard/top-seekers?limit=${newLimit}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setTopSeekers(response.data.seekers);
      setTopSeekersLimit(newLimit);
    } catch (error) {
      console.error('Error loading more seekers:', error);
      toast.error('Failed to load more seekers');
    } finally {
      setLoadingMoreSeekers(false);
    }
  };

  const loadMoreSeekerSignups = async () => {
    const newLimit = latestSignupsSeekersLimit + 10;
    setLoadingMoreSeekerSignups(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get(`/api/admin/dashboard/latest-signups?role=seeker&limit=${newLimit}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setLatestSignupsSeekers(response.data.signups);
      setLatestSignupsSeekersLimit(newLimit);
    } catch (error) {
      console.error('Error loading more seeker signups:', error);
      toast.error('Failed to load more job seekers');
    } finally {
      setLoadingMoreSeekerSignups(false);
    }
  };

  const loadMoreEmployerSignups = async () => {
    const newLimit = latestSignupsEmployersLimit + 10;
    setLoadingMoreEmployerSignups(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get(`/api/admin/dashboard/latest-signups?role=employer&limit=${newLimit}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setLatestSignupsEmployers(response.data.signups);
      setLatestSignupsEmployersLimit(newLimit);
    } catch (error) {
      console.error('Error loading more employer signups:', error);
      toast.error('Failed to load more employers');
    } finally {
      setLoadingMoreEmployerSignups(false);
    }
  };

  const loadMoreJobs = async () => {
    const newLimit = recentJobsLimit + 10;
    setLoadingMoreJobs(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.get(`/api/admin/dashboard/recent-jobs?limit=${newLimit}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setRecentJobs(response.data.jobs);
      setRecentJobsLimit(newLimit);
    } catch (error) {
      console.error('Error loading more jobs:', error);
      toast.error('Failed to load more jobs');
    } finally {
      setLoadingMoreJobs(false);
    }
  };

  const viewVerificationDocs = async (verification) => {
    setSelectedVerification(verification);
    setShowVerificationModal(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await api.get(`/api/admin/verification/${verification.id}/document-urls`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      if (res.data?.urls) {
        setSelectedVerification(prev => prev?.id === verification.id ? { ...prev, ...res.data.urls } : prev);
      }
    } catch {
      // Fall back to existing (possibly expired) URLs
    }
  };

  // Phone call verification dialog state
  const [phoneApproveDialog, setPhoneApproveDialog] = useState({ open: false, requestId: null, verification: null, phoneConfirmed: false, adminNotes: '' });

  const initiateApproveVerification = (requestId, verification) => {
    const userRole = verification?.user?.role;
    if (userRole === 'employer' || userRole === 'owner') {
      // Employer: show phone call confirmation dialog
      setPhoneApproveDialog({ open: true, requestId, verification, phoneConfirmed: false, adminNotes: '' });
    } else {
      // Job seeker: approve directly
      handleApproveVerification(requestId, {});
    }
  };

  const handleApproveVerification = async (requestId, body = {}) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/dashboard/approve-verification/${requestId}`, body, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('Verification approved!');
      setPhoneApproveDialog({ open: false, requestId: null, verification: null, phoneConfirmed: false, adminNotes: '' });
      fetchDashboardData();
      if (showAllVerificationsModal) {
        fetchAllVerifications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve verification');
    }
  };

  const handleRejectVerification = async (requestId, reason) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/dashboard/reject-verification/${requestId}`,
        { reason: reason || 'Your ID verification was rejected. Please resubmit with clearer photos.' },
        { headers: { Authorization: `Bearer ${session.session.access_token}` } }
      );
      toast.success('Verification rejected');
      closeRejectDialog();
      fetchDashboardData();
      if (showAllVerificationsModal) {
        fetchAllVerifications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject verification');
    }
  };

  const confirmRejectVerification = () => {
    if (rejectDialog.requestId) {
      handleRejectVerification(rejectDialog.requestId, rejectDialog.reason);
    }
  };

  const [banUserDialog, setBanUserDialog] = useState({ open: false, user: null, userId: null });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null, userId: null });
  const [deletingUser, setDeletingUser] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, requestId: null, reason: '' });

  const openRejectDialog = (requestId) => {
    setRejectDialog({ open: true, requestId, reason: '' });
  };

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, requestId: null, reason: '' });
  };

  const handleBanUser = (userId, userName) => {
    setBanUserDialog({ open: true, user: userName, userId });
  };

  const confirmBanUser = async () => {
    const { userId } = banUserDialog;

    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/dashboard/ban-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('User banned successfully');
      setBanUserDialog({ open: false, user: null, userId: null });
      fetchDashboardData();
      if (showAllReportsModal) {
        fetchAllReports();
      }
      if (showAllSignupsModal) {
        fetchAllSignups(signupSearchQuery);
      }
      // Update selected profile if open
      if (selectedProfile && selectedProfile.id === userId) {
        setSelectedProfile(prev => prev ? { ...prev, banned: true } : prev);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleDeleteUser = (userId, userName) => {
    setDeleteUserDialog({ open: true, user: userName, userId });
  };

  const confirmDeleteUser = async () => {
    const { userId } = deleteUserDialog;
    setDeletingUser(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.delete(`/api/admin/dashboard/delete-user/${userId}`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('User permanently deleted');
      setDeleteUserDialog({ open: false, user: null, userId: null });
      // Close the profile modal if it's showing the account we just removed.
      if (selectedProfile && selectedProfile.id === userId) {
        setSelectedProfile(null);
      }
      fetchDashboardData();
      if (showAllSignupsModal) {
        fetchAllSignups(signupSearchQuery);
      }
      if (showAllReportsModal) {
        fetchAllReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleUnbanUser = async (userId, userName) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/dashboard/unban-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success(`${userName || 'User'} has been unbanned`);
      fetchDashboardData();
      if (showAllSignupsModal) {
        fetchAllSignups(signupSearchQuery);
      }
      if (showAllReportsModal) {
        fetchAllReports();
      }
      // Update selected profile if open
      if (selectedProfile && selectedProfile.id === userId) {
        setSelectedProfile(prev => prev ? { ...prev, banned: false } : prev);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleDismissReport = async (reportId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.post(`/api/admin/dashboard/dismiss-report/${reportId}`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success('Report dismissed');
      fetchDashboardData();
      if (showAllReportsModal) {
        fetchAllReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss report');
    }
  };

  const getChangeIndicator = (current, previous) => {
    // If no previous data, show "New" badge
    if (previous === undefined || previous === null || previous === 0) {
      if (current && current > 0) {
        return (
          <div className="flex items-center gap-1 text-xs">
            <Badge variant="outline" className="text-xs text-blue-600">New</Badge>
          </div>
        );
      }
      return null;
    }
    
    const change = ((current - previous) / previous) * 100;
    const isPositive = change > 0;
    
    return (
      <div className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
        {Math.abs(change).toFixed(1)}%
      </div>
    );
  };
  
  const handleSort = (table, key) => {
    setSortConfig(prev => ({
      ...prev,
      [table]: {
        key,
        direction: prev[table].key === key && prev[table].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };
  
  const sortData = (data, table) => {
    if (!data || data.length === 0) return data;
    const { key, direction } = sortConfig[table];
    
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle nested objects
      if (key.includes('.')) {
        const keys = key.split('.');
        aVal = keys.reduce((obj, k) => obj?.[k], a);
        bVal = keys.reduce((obj, k) => obj?.[k], b);
      }
      
      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Compare
      if (typeof aVal === 'string') {
        return direction === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };
  
  const SortableHeader = ({ table, column, label }) => (
    <th 
      className="py-3 px-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(table, column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={14} className="text-muted-foreground" />
      </div>
    </th>
  );

  if (loading) {
    const progressPercent = loadingProgress.total > 0 
      ? Math.round((loadingProgress.current / loadingProgress.total) * 100)
      : 0;
    
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-6 spinner" />
          <h2 className="text-2xl font-heading font-bold mb-2">Loading CEO Dashboard</h2>
          <p className="text-muted-foreground mb-6">
            Fetching data from {loadingProgress.total} sources...
          </p>
          
          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-3 mb-2 overflow-hidden">
            <div 
              className="bg-primary h-full transition-all duration-300 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {loadingProgress.current} of {loadingProgress.total} complete ({progressPercent}%)
          </p>
        </div>
      </div>
    );
  }

  // Renders one "Latest Signups" card. Used twice — once for job seekers, once for
  // employers — so the structure stays identical and stylistic drift is impossible.
  // sortKey must match one of the keys in sortConfig (signupsSeekers | signupsEmployers).
  const renderSignupsCard = ({
    title,
    testId,
    signups,
    sortKey,
    emptyText,
    onViewAll,
    onLoadMore,
    loadingMore,
  }) => (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={onViewAll}
          data-testid={`${testId}-view-all`}
        >
          View All
        </Button>
      </CardHeader>
      <CardContent>
        {signups.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{emptyText}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <SortableHeader table={sortKey} column="name" label="Name" />
                    <SortableHeader table={sortKey} column="email" label="Email" />
                    <SortableHeader table={sortKey} column="role" label="Role" />
                    <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort(sortKey, 'town')}>Town</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort(sortKey, 'created_at')}>Joined</th>
                    <SortableHeader table={sortKey} column="verified" label="Status" />
                    <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortData(signups, sortKey).map((signup) => {
                    const isEmp = signup.role === 'employer' || signup.role === 'owner';
                    return (
                      <tr key={signup.id} className={`${signup.banned ? 'bg-red-50/50' : isEmp ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-muted/50'}`}>
                        <td
                          className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                          onClick={() => fetchProfileDetails(signup.id)}
                          data-testid="clickable-name"
                        >
                          {isEmp ? (signup.business_name || signup.name) : signup.name}
                        </td>
                        <td className="py-3 px-4 text-sm break-all max-w-[200px]">{signup.email}</td>
                        <td className="py-3 px-4">
                          <Badge variant="secondary" className={`text-xs whitespace-nowrap ${isEmp ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}`}>{signup.role}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm hidden md:table-cell">{signup.town || 'N/A'}</td>
                        <td className="py-3 px-4 text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                          {new Date(signup.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {signup.banned ? (
                              <Badge variant="destructive" className="text-xs whitespace-nowrap">Banned</Badge>
                            ) : signup.verified ? (
                              <Badge className="bg-green-500 text-xs whitespace-nowrap">Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs whitespace-nowrap">Not Verified</Badge>
                            )}
                            {signup.peso_office && (
                              <PesoBadge officeName={signup.peso_office_name} color={signup.peso_office_color} />
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {signup.banned ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs whitespace-nowrap"
                              onClick={() => handleUnbanUser(signup.id, signup.name)}
                            >
                              Unban
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="text-xs whitespace-nowrap"
                              onClick={() => handleBanUser(signup.id, isEmp ? (signup.business_name || signup.name) : signup.name)}
                            >
                              <Ban size={14} className="mr-1" />
                              Ban
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : `Load More (Showing ${signups.length})`}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className={hideNavigation ? "" : "min-h-screen-safe pb-nav md:pb-0 bg-muted/30"}>
      {!hideNavigation && <TopBar profile={profile} setProfile={setProfile} />}

      <div className={hideNavigation ? "py-0" : "px-4 py-6 max-w-[1600px] mx-auto"}>
        {/* Hero Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-2">CEO War Room 🎯</h1>
          <p className="text-muted-foreground">Real-time business intelligence and operations dashboard</p>
        </div>

        {/* TOP ROW - 3 BIG NUMBER CARDS (Removed Total Users and Active Users - now in User Breakdown widget) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Paying Users */}
          <Card data-testid="metric-paying-users">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Paying Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-1">{metrics?.paying_users_this_month || 0}</div>
              {getChangeIndicator(metrics?.paying_users_this_month, metrics?.paying_users_last_month)}
            </CardContent>
          </Card>

          {/* Revenue This Month */}
          <Card data-testid="metric-revenue">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Revenue (This Month)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-1">₱{(metrics?.revenue_this_month || 0).toLocaleString()}</div>
              {getChangeIndicator(metrics?.revenue_this_month, metrics?.revenue_last_month)}
            </CardContent>
          </Card>

          {/* SMS Credits (Semaphore) — so I don't have to log into Semaphore to check. */}
          <Card data-testid="metric-sms-credits" className={smsCredits?.available && smsCredits?.low ? 'border-2 border-red-300 bg-red-50 dark:bg-red-950/20' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">SMS Credits (Semaphore)</CardTitle>
            </CardHeader>
            <CardContent>
              {smsCredits == null ? (
                <div className="text-4xl font-bold mb-1 text-muted-foreground">…</div>
              ) : smsCredits.available ? (
                <>
                  <div className={`text-4xl font-bold mb-1 ${smsCredits.low ? 'text-red-600' : ''}`}>
                    {(smsCredits.credit_balance || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {smsCredits.low
                      ? `⚠️ Low — top up soon (≤ ${(smsCredits.threshold || 0).toLocaleString()})`
                      : 'credits remaining'}
                    {smsCredits.stale ? ' · cached' : ''}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold mb-1 text-muted-foreground">—</div>
                  <p className="text-xs text-muted-foreground">Balance unavailable</p>
                </>
              )}
            </CardContent>
          </Card>

        </div>

        {/* USER ANALYTICS ROW */}
        <div className="grid grid-cols-1 gap-4 mb-8">
          {/* User Statistics */}
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">👥</span>
                User Breakdown
              </CardTitle>
              <p className="text-sm text-muted-foreground">Account types in the system</p>
            </CardHeader>
            <CardContent>
              {promoStats ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-100 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{promoStats.user_stats.employers}</p>
                    <p className="text-sm text-blue-700">Employers</p>
                    <p className="text-xs text-blue-500">{promoStats.user_stats.verified_employers} verified</p>
                  </div>
                  <div className="text-center p-3 bg-green-100 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{promoStats.user_stats.job_seekers}</p>
                    <p className="text-sm text-green-700">Job Seekers</p>
                  </div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg">
                    <p className="text-2xl font-bold text-slate-600">{promoStats.user_stats.total_users}</p>
                    <p className="text-sm text-slate-700">Total Users</p>
                  </div>
                </div>
              ) : (
                <div className="animate-pulse h-24 bg-blue-100 rounded" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* SECOND ROW - 2 CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {/* User Growth Chart */}
          <Card data-testid="chart-user-growth">
            <CardHeader>
              <CardTitle className="text-base">User Growth (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(value) => formatManilaDate(value, { day: 'numeric' })} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    labelFormatter={(value) => formatManilaDate(value)}
                  />
                  <Line type="monotone" dataKey="users" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="mb-8" data-testid="chart-revenue-history">
          <CardHeader>
            <CardTitle>Revenue (Last 12 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={revenueHistory}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip formatter={(value) => `₱${Number(value).toLocaleString()}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>


          {/* Users by Town Chart */}
          <Card data-testid="chart-users-by-town">
            <CardHeader>
              <CardTitle className="text-base">Top 10 Towns</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={usersByTown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="town" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Bar dataKey="users" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* THIRD ROW - 6 TABLES */}
        <div className="grid grid-cols-1 gap-6">
          {/* Latest Signups — Job Seekers */}
          {renderSignupsCard({
            title: 'Latest Job Seeker Signups (Last 7 Days)',
            testId: 'table-latest-signups-seekers',
            signups: latestSignupsSeekers,
            sortKey: 'signupsSeekers',
            emptyText: 'No new job seeker signups in last 7 days',
            onViewAll: () => fetchAllSignups('', 'seeker'),
            onLoadMore: loadMoreSeekerSignups,
            loadingMore: loadingMoreSeekerSignups,
          })}

          {/* Latest Signups — Employers */}
          {renderSignupsCard({
            title: 'Latest Employer Signups (Last 7 Days)',
            testId: 'table-latest-signups-employers',
            signups: latestSignupsEmployers,
            sortKey: 'signupsEmployers',
            emptyText: 'No new employer signups in last 7 days',
            onViewAll: () => fetchAllSignups('', 'employer'),
            onLoadMore: loadMoreEmployerSignups,
            loadingMore: loadingMoreEmployerSignups,
          })}

          {/* Recently Posted Jobs */}
          <Card data-testid="table-recent-jobs">
            <CardHeader>
              <CardTitle>Recently Posted Jobs (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              {recentJobs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No jobs posted in last 7 days</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead className="border-b bg-muted/30">
                        <tr>
                          <SortableHeader table="recentJobs" column="title" label="Job Title" />
                          <SortableHeader table="recentJobs" column="post_type" label="Type" />
                          <th className="py-3 px-4 text-left text-sm font-semibold cursor-pointer hover:bg-muted/50" onClick={() => handleSort('recentJobs', 'employer.name')}>Employer</th>
                          <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('recentJobs', 'location')}>Location</th>
                          <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('recentJobs', 'salary_min')}>Salary</th>
                          <SortableHeader table="recentJobs" column="status" label="Status" />
                          <th className="py-3 px-4 text-left text-sm font-semibold hidden xl:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('recentJobs', 'created_at')}>Posted</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sortData(recentJobs, 'recentJobs').map((job) => (
                          <tr key={job.id} className="hover:bg-muted/50">
                            <td className="py-3 px-4">
                              <a
                                href={`/jobs/${job.slug || job.id}`}
                                className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                              >
                                {job.title}
                                <ExternalLink size={12} />
                              </a>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {job.post_type === 'job' ? 'Job' : 'Gig'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm whitespace-nowrap">
                              {job.employer?.business_name || job.employer?.name || 'Unknown'}
                              {job.employer?.verified && (
                                <span className="ml-1 text-blue-500" title="Verified">✓</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm hidden md:table-cell">{job.location}</td>
                            <td className="py-3 px-4 text-sm hidden lg:table-cell whitespace-nowrap">
                              {job.salary_min && job.salary_max
                                ? `₱${job.salary_min.toLocaleString()} - ₱${job.salary_max.toLocaleString()}`
                                : job.salary_min
                                  ? `₱${job.salary_min.toLocaleString()}+`
                                  : 'N/A'
                              }
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant={job.status === 'open' ? 'default' : 'secondary'}
                                className="text-xs whitespace-nowrap"
                              >
                                {job.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-xs text-muted-foreground hidden xl:table-cell whitespace-nowrap">
                              {new Date(job.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMoreJobs}
                      disabled={loadingMoreJobs}
                    >
                      {loadingMoreJobs ? 'Loading...' : `Load More (Showing ${recentJobs.length})`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Pending ID Verifications */}
          <Card data-testid="table-pending-verifications">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pending ID Verifications</CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={fetchAllVerifications}
                data-testid="show-all-verifications-btn"
              >
                Show All
              </Button>
            </CardHeader>
            <CardContent>
              {pendingVerifications.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No pending verifications</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead className="border-b bg-muted/30">
                      <tr>
                        <SortableHeader table="verifications" column="user.name" label="Name" />
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('verifications', 'user.email')}>Email</th>
                        <SortableHeader table="verifications" column="user.role" label="Role" />
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell">Phone</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('verifications', 'created_at')}>Uploaded</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold">Documents</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortData(pendingVerifications, 'verifications').map((verification) => {
                        const isEmpV = verification.user?.role === 'employer' || verification.user?.role === 'owner';
                        return (
                        <tr key={verification.id} className={isEmpV ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-muted/50'}>
                          <td
                            className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                            onClick={() => fetchProfileDetails(verification.user_id)}
                            data-testid="clickable-name"
                          >
                            {isEmpV ? (verification.user?.business_name || verification.user?.name || 'Unknown') : (verification.user?.name || 'Unknown')}
                          </td>
                          <td className="py-3 px-4 text-sm hidden md:table-cell break-all max-w-[180px]">{verification.user?.email || 'N/A'}</td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary" className={`text-xs whitespace-nowrap ${isEmpV ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}`}>{verification.user?.role}</Badge>
                          </td>
                          <td className="py-3 px-4 text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                            {verification.user?.phone ? (
                              <a href={`tel:${verification.user.phone}`} className="text-primary hover:underline flex items-center gap-1">
                                <Phone size={12} />
                                {verification.user.phone}
                              </a>
                            ) : <span className="text-red-500">No phone</span>}
                          </td>
                          <td className="py-3 px-4 text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                            {new Date(verification.created_at).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs whitespace-nowrap"
                              onClick={() => viewVerificationDocs(verification)}
                              data-testid="view-docs-btn"
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-xs whitespace-nowrap"
                                onClick={() => initiateApproveVerification(verification.id, verification)}
                                data-testid="approve-verification-btn"
                              >
                                <Check size={14} className="mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs whitespace-nowrap"
                                onClick={() => openRejectDialog(verification.id)}
                                data-testid="reject-verification-btn"
                              >
                                <X size={14} className="mr-1" />
                                Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scam Reports */}
          <Card data-testid="table-scam-reports">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Scam Reports (Last 7 Days)</CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={fetchAllReports}
                data-testid="show-all-reports-btn"
              >
                Show All
              </Button>
            </CardHeader>
            <CardContent>
              {recentReports.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No reports in last 7 days</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="border-b bg-muted/30">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('reports', 'reporter.name')}>Reporter</th>
                        <SortableHeader table="reports" column="reported_user.name" label="Reported User" />
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('reports', 'reason')}>Reason</th>
                        <SortableHeader table="reports" column="post.title" label="Post" />
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden xl:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('reports', 'created_at')}>Reported At</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortData(recentReports, 'reports').map((report) => (
                        <tr key={report.id} className="hover:bg-muted/50">
                          <td 
                            className="py-3 px-4 text-sm cursor-pointer hover:underline hidden md:table-cell whitespace-nowrap"
                            onClick={() => report.reporter_id && fetchProfileDetails(report.reporter_id)}
                          >
                            {report.reporter?.name || 'Anonymous'}
                          </td>
                          <td 
                            className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                            onClick={() => report.reported_user_id && fetchProfileDetails(report.reported_user_id)}
                            data-testid="clickable-name"
                          >
                            {report.reported_user?.name || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-sm hidden lg:table-cell max-w-[150px] truncate" title={report.reason || 'N/A'}>{report.reason || 'N/A'}</td>
                          <td className="py-3 px-4">
                            {report.post ? (
                              <span 
                                className="text-sm text-primary cursor-pointer hover:underline inline-flex items-center gap-1 max-w-[200px] truncate"
                                onClick={() => navigateToReportedPost(report)}
                                title={report.post.title}
                              >
                                {report.post.title}
                                <ExternalLink size={12} className="flex-shrink-0" />
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">No post</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs text-muted-foreground hidden xl:table-cell whitespace-nowrap">
                            {new Date(report.created_at).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs whitespace-nowrap"
                                onClick={() => handleBanUser(report.reported_user_id, report.reported_user?.name || 'Unknown User')}
                                data-testid="ban-user-btn"
                                title={`Ban ${report.reported_user?.name || 'Reported User'}`}
                              >
                                <Ban size={14} className="mr-1" />
                                Ban
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs whitespace-nowrap"
                                onClick={() => handleDismissReport(report.id)}
                                data-testid="dismiss-report-btn"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Employer Homepage Banners (moderation queue) */}
          <AdminBannersSection />

          {/* Services Marketplace: reported listings moderation */}
          <AdminServiceModerationSection />

          {/* Services Marketplace: aggregate analytics (W4) */}
          <AdminServicesAnalyticsSection />

          {/* Top Seekers */}
          <Card data-testid="table-top-seekers">
            <CardHeader>
              <CardTitle>Top Seekers by Applications (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              {topSeekers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No data available</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead className="border-b bg-muted/30">
                        <tr>
                          <SortableHeader table="seekers" column="rank" label="Rank" />
                          <SortableHeader table="seekers" column="name" label="Name" />
                          <SortableHeader table="seekers" column="applications" label="Applications" />
                          <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('seekers', 'rating')}>Rating</th>
                          <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('seekers', 'town')}>Town</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sortData(topSeekers, 'seekers').map((seeker) => (
                          <tr key={seeker.id} className="hover:bg-muted/50">
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="text-xs whitespace-nowrap">#{seeker.rank}</Badge>
                            </td>
                            <td
                              className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                              onClick={() => fetchProfileDetails(seeker.id)}
                              data-testid="clickable-name"
                            >
                              {seeker.name}
                            </td>
                            <td className="py-3 px-4 text-sm">{seeker.applications}</td>
                            <td className="py-3 px-4 text-sm hidden md:table-cell whitespace-nowrap">⭐ {seeker.rating.toFixed(1)}</td>
                            <td className="py-3 px-4 text-sm hidden lg:table-cell">{seeker.town}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMoreSeekers}
                      disabled={loadingMoreSeekers}
                    >
                      {loadingMoreSeekers ? 'Loading...' : `Load More (Showing ${topSeekers.length})`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Top Spenders */}
          <Card data-testid="table-top-spenders">
            <CardHeader>
              <CardTitle>Top 10 Employers by Revenue (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              {topSpenders.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No data available</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="border-b bg-muted/30">
                      <tr>
                        <SortableHeader table="spenders" column="rank" label="Rank" />
                        <SortableHeader table="spenders" column="name" label="Name" />
                        <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('spenders', 'town')}>Town</th>
                        <SortableHeader table="spenders" column="posts_count" label="Posts" />
                        <SortableHeader table="spenders" column="revenue_generated" label="Revenue" />
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortData(topSpenders, 'spenders').map((spender) => (
                        <tr key={spender.id} className="hover:bg-muted/50">
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-xs whitespace-nowrap">#{spender.rank}</Badge>
                          </td>
                          <td 
                            className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                            onClick={() => fetchProfileDetails(spender.id)}
                            data-testid="clickable-name"
                          >
                            {spender.name}
                          </td>
                          <td className="py-3 px-4 text-sm hidden md:table-cell">{spender.town}</td>
                          <td className="py-3 px-4 text-sm">{spender.posts_count}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-primary whitespace-nowrap">
                            ₱{spender.revenue_generated.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Verification Modal */}
      <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ID Verification Documents</DialogTitle>
          </DialogHeader>
          {selectedVerification && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ID Front */}
              <div>
                <p className="text-sm font-medium mb-2">ID Front</p>
                {selectedVerification.id_front_url ? (
                  <img 
                    src={selectedVerification.id_front_url} 
                    alt="ID Front" 
                    className="w-full rounded-lg border"
                  />
                ) : (
                  <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                    No image
                  </div>
                )}
              </div>

              {/* ID Back */}
              <div>
                <p className="text-sm font-medium mb-2">ID Back</p>
                {selectedVerification.id_back_url ? (
                  <img 
                    src={selectedVerification.id_back_url} 
                    alt="ID Back" 
                    className="w-full rounded-lg border"
                  />
                ) : (
                  <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                    No image
                  </div>
                )}
              </div>

              {/* Selfie */}
              <div>
                <p className="text-sm font-medium mb-2">Selfie</p>
                {selectedVerification.selfie_url ? (
                  <img 
                    src={selectedVerification.selfie_url} 
                    alt="Selfie" 
                    className="w-full rounded-lg border"
                  />
                ) : (
                  <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                    No image
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedVerification?.admin_notes && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg border">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Admin Notes</p>
              <p className="text-sm whitespace-pre-wrap">{selectedVerification.admin_notes}</p>
            </div>
          )}
          {selectedVerification?.phone_verified && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700">
              <Phone size={12} />
              Phone verified by admin
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowVerificationModal(false)}>Close</Button>
            {selectedVerification?.status === 'pending' && (
              <>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    initiateApproveVerification(selectedVerification.id, selectedVerification);
                    setShowVerificationModal(false);
                  }}
                >
                  <Check size={16} className="mr-1" />
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowVerificationModal(false);
                    openRejectDialog(selectedVerification.id);
                  }}
                >
                  <X size={16} className="mr-1" />
                  Reject
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Profile - Full Details</DialogTitle>
          </DialogHeader>
          {selectedProfile && (
            <div className="space-y-6">
              {/* Header Section */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <Avatar className="w-20 h-20">
                  {selectedProfile.avatar_url ? (
                    <img src={selectedProfile.avatar_url} alt={selectedProfile.name} className="w-full h-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-2xl">{selectedProfile.name?.[0]}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedProfile.name || 'No name'}</h3>
                  <p className="text-sm text-muted-foreground">{selectedProfile.email || 'No email'}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="secondary">{selectedProfile.role || 'Unknown role'}</Badge>
                    {selectedProfile.verified && (
                      <Badge className="bg-green-500">✓ Verified</Badge>
                    )}
                    {selectedProfile.banned && (
                      <Badge variant="destructive">Banned</Badge>
                    )}
                  </div>
                  {/* Manual Verify Button — show for unverified employers/owners */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {!selectedProfile.verified && (selectedProfile.role === 'employer' || selectedProfile.role === 'owner') && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => setShowManualVerifyDialog(true)}
                      >
                        <Check size={14} className="mr-1" />
                        Verify Employer
                      </Button>
                    )}
                    {selectedProfile.banned ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnbanUser(selectedProfile.id, selectedProfile.name)}
                      >
                        Unban User
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleBanUser(selectedProfile.id, selectedProfile.name)}
                      >
                        <Ban size={14} className="mr-1" />
                        Ban User
                      </Button>
                    )}
                    {/* Permanently delete (for removing test accounts). Hidden for
                        privileged accounts; the backend + DB function also refuse those. */}
                    {selectedProfile.role !== 'admin' && selectedProfile.role !== 'peso_staff' && (
                      <Button
                        size="sm"
                        className="bg-red-700 hover:bg-red-800 text-white"
                        onClick={() => handleDeleteUser(selectedProfile.id, selectedProfile.name || selectedProfile.business_name || selectedProfile.email)}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="text-xs font-mono">{selectedProfile.id}</p>
                </div>
              </div>

              {/* Basic Info Section */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Basic Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Phone</p>
                    <p className="text-sm font-medium">{selectedProfile.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Town/Location</p>
                    <p className="text-sm font-medium">{selectedProfile.town || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Barangay</p>
                    <p className="text-sm font-medium">{selectedProfile.barangay || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Address</p>
                    <p className="text-sm font-medium">{selectedProfile.address || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Date of Birth</p>
                    <p className="text-sm font-medium">{selectedProfile.date_of_birth ? new Date(selectedProfile.date_of_birth).toLocaleDateString() : 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Rating</p>
                    <p className="text-sm font-medium">⭐ {(selectedProfile.rating || 0).toFixed(1)} ({selectedProfile.review_count || 0} reviews)</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Credits Balance</p>
                    <p className="text-sm font-medium">{selectedProfile.credits || 0}</p>
                  </div>
                </div>
              </div>

              {/* Account Dates */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Account Timeline</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Created At</p>
                    <p className="text-sm font-medium">{selectedProfile.created_at ? new Date(selectedProfile.created_at).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Last Updated</p>
                    <p className="text-sm font-medium">{selectedProfile.updated_at ? new Date(selectedProfile.updated_at).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Referral Code</p>
                    <p className="text-sm font-mono">{selectedProfile.referral_code || 'None'}</p>
                  </div>
                  {selectedProfile.role === 'job_seeker' && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Ambassador</p>
                      <button
                        onClick={async () => {
                          try {
                            const { data: session } = await supabase.auth.getSession();
                            if (!session?.session?.access_token) { toast.error('Session expired'); return; }
                            const newState = !selectedProfile.is_referrer;
                            const res = await api.post('/api/admin/referrers/toggle', {
                              user_id: selectedProfile.id,
                              enable: newState
                            }, { headers: { Authorization: `Bearer ${session.session.access_token}` } });
                            setSelectedProfile(prev => ({
                              ...prev,
                              is_referrer: newState,
                              referral_code: res.data.referral_code || prev.referral_code
                            }));
                            toast.success(newState ? 'Ambassador enabled' : 'Ambassador disabled');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to toggle ambassador');
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          selectedProfile.is_referrer
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {selectedProfile.is_referrer ? 'Active Ambassador' : 'Enable Ambassador'}
                      </button>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Terms Agreed</p>
                    <p className="text-sm font-medium">{selectedProfile.terms_agreed_at ? new Date(selectedProfile.terms_agreed_at).toLocaleString() : 'Not agreed'}</p>
                  </div>
                  {selectedProfile.referred_by && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Referred By</p>
                      <p className="text-sm font-mono">{selectedProfile.referred_by}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Bio / Tagline */}
              {(selectedProfile.bio || selectedProfile.tagline) && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Bio & Tagline</h4>
                  <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                    {selectedProfile.tagline && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Tagline</p>
                        <p className="text-sm italic">"{selectedProfile.tagline}"</p>
                      </div>
                    )}
                    {selectedProfile.bio && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Bio / Experience</p>
                        <p className="text-sm whitespace-pre-wrap">{selectedProfile.bio}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Job Seeker Specific Fields */}
              {(selectedProfile.role === 'job_seeker' || selectedProfile.role === 'contractor') && (
                <>
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Job Seeker Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Years of Experience</p>
                        <p className="text-sm font-medium">{selectedProfile.years_of_experience || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Education Level</p>
                        <p className="text-sm font-medium">{getEducationLabel(selectedProfile.education_level) || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Field of Study</p>
                        <p className="text-sm font-medium">{selectedProfile.education_field || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Availability</p>
                        <p className="text-sm font-medium">{selectedProfile.availability || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Salary Range ({selectedProfile.salary_expectation_type || 'monthly'})</p>
                        <p className="text-sm font-medium">
                          {selectedProfile.salary_min || selectedProfile.salary_max
                            ? `₱${(selectedProfile.salary_min || 0).toLocaleString()} - ₱${(selectedProfile.salary_max || 0).toLocaleString()}`
                            : 'Not set'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Availability Dates */}
                  {selectedProfile.availability_dates && selectedProfile.availability_dates.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Availability Dates</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.availability_dates.map((date, i) => (
                          <Badge key={i} variant="secondary">{new Date(date).toLocaleDateString()}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  {selectedProfile.skills && selectedProfile.skills.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.skills.map((skill, i) => (
                          <Badge key={i} variant="outline">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preferred Locations */}
                  {selectedProfile.preferred_locations && selectedProfile.preferred_locations.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Preferred Work Locations</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.preferred_locations.map((loc, i) => (
                          <Badge key={i} variant="secondary">{loc}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preferred Work Types */}
                  {selectedProfile.preferred_work_types && selectedProfile.preferred_work_types.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Preferred Work Types</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.preferred_work_types.map((type, i) => (
                          <Badge key={i} variant="secondary">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work History */}
                  {selectedProfile.work_history && selectedProfile.work_history.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Work History</h4>
                      <div className="space-y-3">
                        {selectedProfile.work_history.map((work, i) => (
                          <div key={i} className="bg-muted/30 p-3 rounded-lg">
                            <p className="font-medium text-sm">{work.position || 'Unknown Position'}</p>
                            <p className="text-sm text-muted-foreground">{work.company || 'Unknown Company'}</p>
                            <p className="text-xs text-muted-foreground">{work.duration || 'No duration'}</p>
                            {work.description && <p className="text-sm mt-1">{work.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                      Certifications ({userCertifications.length})
                    </h4>
                    {userCertifications.length === 0 ? (
                      <p className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">No certifications uploaded</p>
                    ) : (
                      <div className="space-y-2">
                        {userCertifications.map((cert) => (
                          <div key={cert.id} className="p-3 border rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm font-medium">{cert.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  Issued: {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : 'N/A'}
                                  {cert.expiry_date && ` | Expires: ${new Date(cert.expiry_date).toLocaleDateString()}`}
                                </p>
                              </div>
                              {cert.file_url && (
                                <a
                                  href={cert.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline"
                                >
                                  View File
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Job Applications */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                      Job Applications {loadingSeekerApps && <span className="text-xs">(loading...)</span>}
                    </h4>
                    {loadingSeekerApps ? (
                      <div className="text-sm text-muted-foreground">Loading applications...</div>
                    ) : seekerApplications.length === 0 ? (
                      <p className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">No applications yet</p>
                    ) : (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {seekerApplications.map((app) => (
                          <div key={app.application_id} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <a
                                  href={`/jobs/${app.job_slug || app.job_id}`}
                                  className="text-sm font-medium text-primary hover:underline"
                                >
                                  {app.job_title}
                                </a>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {app.employer_name} • {app.job_location}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {app.application_status}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    Applied {new Date(app.applied_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Employer Specific Fields */}
              {(selectedProfile.role === 'employer' || selectedProfile.role === 'owner') && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Business Information</h4>
                  {selectedProfile.is_private_household && (
                    <Badge className="mb-3 bg-purple-100 text-purple-700 border-purple-200">Private Household</Badge>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Name</p>
                      <p className="text-sm font-medium">{selectedProfile.business_name || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Type</p>
                      <p className="text-sm font-medium">{selectedProfile.business_type || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Industry</p>
                      <p className="text-sm font-medium">{selectedProfile.industry || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Phone</p>
                      <p className="text-sm font-medium">{selectedProfile.business_phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Business Address</p>
                      <p className="text-sm font-medium">{selectedProfile.business_address || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Employee Count</p>
                      <p className="text-sm font-medium">{selectedProfile.number_of_employees || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Years in Business</p>
                      <p className="text-sm font-medium">{selectedProfile.years_in_business || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Website</p>
                      <p className="text-sm font-medium">
                        {selectedProfile.website ? (
                          <a href={selectedProfile.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {selectedProfile.website}
                          </a>
                        ) : 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Unlimited Reveals</p>
                      <p className="text-sm font-medium">
                        {selectedProfile.unlimited_reveals ? (
                          <span className="text-green-600">✓ Active until {selectedProfile.unlimited_reveals_until ? new Date(selectedProfile.unlimited_reveals_until).toLocaleDateString() : 'N/A'}</span>
                        ) : (
                          <span className="text-muted-foreground">Not active</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {selectedProfile.business_description && (
                    <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Business Description</p>
                      <p className="text-sm whitespace-pre-wrap">{selectedProfile.business_description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Data (for debugging - optional) */}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View Raw Profile Data</summary>
                <pre className="mt-2 p-3 bg-muted rounded-lg overflow-x-auto text-xs">
                  {JSON.stringify(selectedProfile, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Verify Confirmation Dialog */}
      <Dialog open={showManualVerifyDialog} onOpenChange={setShowManualVerifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verify Employer</DialogTitle>
            <DialogDescription>
              Verify <strong>{selectedProfile?.name}</strong> without ID upload? This grants:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-1 ml-4 list-disc">
            <li>Verified True Boholano badge</li>
            <li>Referral reward to referrer (if applicable)</li>
          </ul>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowManualVerifyDialog(false)} disabled={manualVerifying}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleManualVerify} disabled={manualVerifying}>
              {manualVerifying ? 'Verifying...' : 'Confirm Verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Details Modal */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Post Details</DialogTitle>
          </DialogHeader>
          {selectedPost && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2">{selectedPost.title}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <MapPin size={14} />
                  {selectedPost.location}
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Description</p>
                <p className="text-sm">{selectedPost.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* All Reports Modal */}
      <Dialog open={showAllReportsModal} onOpenChange={setShowAllReportsModal}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Scam Reports</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="border-b bg-muted/30 sticky top-0 bg-card">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('allReports', 'reporter.name')}>Reporter</th>
                  <SortableHeader table="allReports" column="reported_user.name" label="Reported User" />
                  <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('allReports', 'reason')}>Reason</th>
                  <SortableHeader table="allReports" column="post.title" label="Post" />
                  <th className="py-3 px-4 text-left text-sm font-semibold hidden xl:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('allReports', 'created_at')}>Reported At</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortData(allReports, 'allReports').map((report) => (
                  <tr key={report.id} className="hover:bg-muted/50">
                    <td 
                      className="py-3 px-4 text-sm cursor-pointer hover:underline hidden md:table-cell whitespace-nowrap"
                      onClick={() => report.reporter_id && fetchProfileDetails(report.reporter_id)}
                    >
                      {report.reporter?.name || 'Anonymous'}
                    </td>
                    <td 
                      className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                      onClick={() => report.reported_user_id && fetchProfileDetails(report.reported_user_id)}
                    >
                      {report.reported_user?.name || 'Unknown'}
                    </td>
                    <td className="py-3 px-4 text-sm hidden lg:table-cell max-w-[150px] truncate" title={report.reason || 'N/A'}>{report.reason || 'N/A'}</td>
                    <td className="py-3 px-4">
                      {report.post ? (
                        <span 
                          className="text-sm text-primary cursor-pointer hover:underline inline-flex items-center gap-1 max-w-[200px] truncate"
                          onClick={() => navigateToReportedPost(report)}
                          title={report.post.title}
                        >
                          {report.post.title}
                          <ExternalLink size={12} className="flex-shrink-0" />
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No post</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground hidden xl:table-cell whitespace-nowrap">
                      {new Date(report.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="text-xs whitespace-nowrap"
                          onClick={() => handleBanUser(report.reported_user_id, report.reported_user?.name || 'Unknown User')}
                        >
                          <Ban size={14} className="mr-1" />
                          Ban
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs whitespace-nowrap"
                          onClick={() => handleDismissReport(report.id)}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Dismiss
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Verifications Modal */}
      <Dialog open={showAllVerificationsModal} onOpenChange={setShowAllVerificationsModal}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Verification Requests</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="border-b bg-muted/30 sticky top-0 bg-card">
                <tr>
                  <SortableHeader table="allVerifications" column="user.name" label="Name" />
                  <th className="py-3 px-4 text-left text-sm font-semibold hidden md:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('allVerifications', 'user.email')}>Email</th>
                  <SortableHeader table="allVerifications" column="status" label="Status" />
                  <th className="py-3 px-4 text-left text-sm font-semibold hidden lg:table-cell cursor-pointer hover:bg-muted/50" onClick={() => handleSort('allVerifications', 'created_at')}>Uploaded</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Documents</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortData(allVerifications, 'allVerifications').map((verification) => {
                  const isEmpAV = verification.user?.role === 'employer' || verification.user?.role === 'owner';
                  return (
                  <tr key={verification.id} className={isEmpAV ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-muted/50'}>
                    <td
                      className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline whitespace-nowrap"
                      onClick={() => fetchProfileDetails(verification.user_id)}
                    >
                      {isEmpAV ? (verification.user?.business_name || verification.user?.name || 'Unknown') : (verification.user?.name || 'Unknown')}
                    </td>
                    <td className="py-3 px-4 text-sm hidden md:table-cell break-all max-w-[180px]">{verification.user?.email || 'N/A'}</td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={verification.status === 'approved' ? 'default' : verification.status === 'rejected' ? 'destructive' : 'secondary'}
                        className="text-xs whitespace-nowrap"
                      >
                        {verification.status}
                      </Badge>
                      {isEmpAV && <Badge variant="outline" className="text-xs ml-1 bg-blue-50 text-blue-600 border-blue-200">Employer</Badge>}
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                      {new Date(verification.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs whitespace-nowrap"
                        onClick={() => viewVerificationDocs(verification)}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </Button>
                    </td>
                    <td className="py-3 px-4">
                      {verification.status === 'pending' && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-xs whitespace-nowrap"
                            onClick={() => initiateApproveVerification(verification.id, verification)}
                          >
                            <Check size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs whitespace-nowrap"
                            onClick={() => openRejectDialog(verification.id)}
                          >
                            <X size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Signups Modal with Search */}
      <Dialog open={showAllSignupsModal} onOpenChange={(open) => {
        setShowAllSignupsModal(open);
        if (!open) {
          // Reset role filter so the next open from a fresh button starts clean
          setSignupsModalRole(null);
          setSignupSearchQuery('');
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {signupsModalRole === 'seeker' ? 'All Job Seeker Signups' :
               signupsModalRole === 'employer' ? 'All Employer Signups' :
               'All User Signups'}
              {' '}({allSignupsTotal} total)
            </DialogTitle>
          </DialogHeader>

          {/* Search Form */}
          <form onSubmit={handleSignupSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={signupSearchQuery}
                onChange={(e) => setSignupSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" disabled={signupSearchLoading}>
              {signupSearchLoading ? 'Searching...' : 'Search'}
            </Button>
            {signupSearchQuery && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSignupSearchQuery('');
                  fetchAllSignups('');
                }}
              >
                Clear
              </Button>
            )}
          </form>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Name</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Email</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Phone</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Role</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Town</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Status</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Joined</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allSignups.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      {signupSearchQuery ? 'No users found matching your search' : 'No signups found'}
                    </td>
                  </tr>
                ) : (
                  allSignups.map((signup) => {
                    const isEmpAS = signup.role === 'employer' || signup.role === 'owner';
                    return (
                    <tr key={signup.id} className={signup.banned ? 'bg-red-50/50' : 'hover:bg-muted/50'}>
                      <td
                        className="py-3 px-4 text-sm font-medium text-primary cursor-pointer hover:underline"
                        onClick={() => fetchProfileDetails(signup.id)}
                      >
                        {isEmpAS ? (signup.business_name || signup.name || 'Unknown') : (signup.name || 'Unknown')}
                      </td>
                      <td className="py-3 px-4 text-sm break-all max-w-[200px]">{signup.email || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm">{signup.phone || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={isEmpAS ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {signup.role || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">{signup.town || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap items-center gap-1">
                          {signup.banned ? (
                            <Badge variant="destructive" className="text-xs">Banned</Badge>
                          ) : signup.verified ? (
                            <Badge className="bg-green-600 text-xs">Verified</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Unverified</Badge>
                          )}
                          {signup.peso_office && (
                            <PesoBadge officeName={signup.peso_office_name} color={signup.peso_office_color} />
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(signup.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        {signup.banned ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs whitespace-nowrap"
                            onClick={() => handleUnbanUser(signup.id, signup.name)}
                          >
                            Unban
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs whitespace-nowrap"
                            onClick={() => handleBanUser(signup.id, isEmpAS ? (signup.business_name || signup.name) : signup.name)}
                          >
                            <Ban size={14} className="mr-1" />
                            Ban
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ban User Confirmation Dialog */}
      <Dialog open={banUserDialog.open} onOpenChange={(open) => !open && setBanUserDialog({ open: false, user: null, userId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban size={20} />
              Ban User Confirmation
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The user will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">You are about to ban:</h4>
              <p className="text-xl font-bold text-red-700 dark:text-red-300">{banUserDialog.user}</p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-semibold">What happens when you ban a user:</p>
              <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                <li>All their active job/gig posts will be deleted</li>
                <li>They will be immediately logged out</li>
                <li>They won't be able to log in again</li>
                <li>All their applications will be cancelled</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBanUserDialog({ open: false, user: null, userId: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBanUser}
              data-testid="confirm-ban-btn"
            >
              <Ban size={16} className="mr-2" />
              Yes, Ban {banUserDialog.user}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanently Delete User Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => { if (!open && !deletingUser) setDeleteUserDialog({ open: false, user: null, userId: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={20} />
              Permanently Delete User
            </DialogTitle>
            <DialogDescription>
              This permanently removes the account and all of its data. This CANNOT be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">You are about to permanently delete:</h4>
              <p className="text-xl font-bold text-red-700 dark:text-red-300 break-words">{deleteUserDialog.user}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold">This erases, forever:</p>
              <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                <li>Their profile and login account</li>
                <li>Any jobs they posted and applications they made</li>
                <li>Their messages, notifications, saved jobs, and credits</li>
              </ul>
              <p className="text-xs text-muted-foreground pt-1">
                To block someone without erasing their record, use <span className="font-semibold">Ban</span> instead.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={deletingUser}
              onClick={() => setDeleteUserDialog({ open: false, user: null, userId: null })}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              disabled={deletingUser}
              onClick={confirmDeleteUser}
              data-testid="confirm-delete-btn"
            >
              {deletingUser ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Trash2 size={16} className="mr-2" />}
              {deletingUser ? 'Deleting…' : 'Yes, permanently delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Verification Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && closeRejectDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X size={20} />
              Reject Verification
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this verification request. This will be sent to the user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <textarea
                className="w-full min-h-[100px] p-3 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter the reason for rejection (e.g., 'Photo is blurry', 'ID is expired', 'Selfie doesn't match ID')"
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Common reasons: Blurry photo, ID expired, Face doesn't match, Invalid document type
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                The user will receive a notification with this reason and can resubmit their verification.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeRejectDialog}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRejectVerification}
              disabled={!rejectDialog.reason.trim()}
            >
              <X size={16} className="mr-2" />
              Reject Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phone Call Verification Dialog (employers only) */}
      <Dialog open={phoneApproveDialog.open} onOpenChange={(open) => !open && setPhoneApproveDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Phone size={20} />
              Employer Phone Verification
            </DialogTitle>
            <DialogDescription>
              You must call the employer to verify they are authorized to post jobs for this business before approving.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium">Business: {phoneApproveDialog.verification?.user?.business_name || phoneApproveDialog.verification?.user?.name || 'N/A'}</p>
              <p className="text-sm flex items-center gap-1 mt-1">
                <Phone size={14} />
                Phone: <strong>{phoneApproveDialog.verification?.user?.phone || 'No phone on profile'}</strong>
              </p>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="phone-verified"
                checked={phoneApproveDialog.phoneConfirmed}
                onCheckedChange={(checked) => setPhoneApproveDialog(prev => ({ ...prev, phoneConfirmed: checked }))}
              />
              <label htmlFor="phone-verified" className="text-sm font-medium leading-none cursor-pointer">
                I have called and verified this person is authorized to post jobs for this business
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Admin Notes (optional)</label>
              <Textarea
                value={phoneApproveDialog.adminNotes}
                onChange={(e) => setPhoneApproveDialog(prev => ({ ...prev, adminNotes: e.target.value }))}
                placeholder="e.g., Spoke with Jane, confirmed she is the HR manager"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPhoneApproveDialog(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={!phoneApproveDialog.phoneConfirmed}
              onClick={() => handleApproveVerification(phoneApproveDialog.requestId, {
                phone_verified: true,
                admin_notes: phoneApproveDialog.adminNotes || undefined
              })}
            >
              <Check size={16} className="mr-2" />
              Approve Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!hideNavigation && <BottomNav profile={profile} />}
    </div>
  );
};

export default CEODashboard;
